import React, { ReactNode } from 'react'
import styled from '@emotion/styled'
import { L2ErfYPadRow } from './L2ErfRow'

interface ErfRootProps {
	controlTemplateColumns?: string
	contentTemplateColumns?: string
}

export const ErfRoot = styled.div<ErfRootProps>(({
	theme,
	controlTemplateColumns = 'repeat(1, min-content)',
	contentTemplateColumns = 'repeat(1, 1fr)',
}) => ({
	display: 'grid',
	gridTemplateColumns: `[rowstartln] ${controlTemplateColumns} [controlendln] ${contentTemplateColumns} [rowendln]`,
	columnGap: theme.spacing(2),
}))

interface ErfHeaderProps {
	addOutline?: boolean
}

const ErfHeader = styled.div<ErfHeaderProps>(({theme, addOutline}) => ({
	gridColumn: 'rowstartln / rowendln',
	display: 'grid',
	gridTemplateColumns: 'subgrid',
	backgroundColor: theme.colors.second,
	borderTopLeftRadius: theme.borderRadius,
	borderTopRightRadius: theme.borderRadius,
	fontSize: '14px',
	fontWeight: 600,
	outline: addOutline ? `1px solid ${theme.colors.lines}` : undefined,
	position: 'sticky',
	top: 0,
	zIndex: 1,
}))

const ErfControlTitles = styled.div(({theme}) => ({
	gridColumn: 'rowstartln / controlendln',
	display: 'grid',
	gridTemplateColumns: 'subgrid',
	alignItems: 'center',
	justifyItems: 'center',
	textAlign: 'center',
	backgroundColor: theme.colors.third,
	borderTopLeftRadius: theme.borderRadius,
	borderTopRightRadius: theme.borderRadius,
	padding: theme.spacing(1.5, 2),
}))

const ErfContentTitle = styled.div(({theme}) => ({
	padding: theme.spacing(1.5, 0.5),
}))

const ErfContent = styled.div(({theme}) => ({
	gridColumn: 'rowstartln / rowendln',
	display: 'grid',
	gridTemplateColumns: 'subgrid',
	borderBottomLeftRadius: theme.borderRadius,
	borderBottomRightRadius: theme.borderRadius,
	outline: `1px solid ${theme.colors.lines}`,
}))

interface L2ErfProps {
	controlTemplateColumns?: string
	controlTitles?: string[]
	contentTemplateColumns?: string
	contentTitles?: string[]
	children?: ReactNode,
	hideControlTitleColumn?: boolean
	outlineHeader?: boolean,
}

const L2Erf = ({
	controlTemplateColumns,
	controlTitles = [],
	contentTemplateColumns,
	contentTitles = [''],
	children,
	hideControlTitleColumn = false,
	outlineHeader,
}: L2ErfProps) => {
	return (
		<ErfRoot
			// control sizing strategy was originally min-content;
			// max-content supports strings of text better and also doesn't
			// seem to interrupt original workings with radios and checks
			controlTemplateColumns={controlTemplateColumns ?? `repeat(${Math.max(hideControlTitleColumn ? 1 : controlTitles.length, 1)}, max-content)`}
			contentTemplateColumns={contentTemplateColumns ?? `repeat(${Math.max(contentTitles.length, 1)}, auto)`}
		>
			<ErfHeader addOutline={outlineHeader}>
				{
					hideControlTitleColumn === false && (
						<ErfControlTitles>
							{controlTitles.map((title, idx) => <div key={idx}>{title}</div>)}
						</ErfControlTitles>
					)
				}
				{contentTitles.map((title, idx) => (
					<ErfContentTitle key={idx}>
						{title}
					</ErfContentTitle>
				))}
			</ErfHeader>
			<ErfContent>
				{hideControlTitleColumn === false && <L2ErfYPadRow />}
				{children}
				{hideControlTitleColumn === false && <L2ErfYPadRow bottom={true} />}
			</ErfContent>
		</ErfRoot>
	)
}

export default L2Erf
